/**
 * Soft-safe Move 3: app shell — step panels live in src/ui/steps/*.
 * Soft-safe ① shareViewModel sole human face (Summary panel).
 * Soft-safe ② softGate.ts behavior unchanged.
 * Soft-refuse: identity lock held (no metric Soft UI copy). Soft 14 ops-only.
 * Soft-safe ③: Soft #2 line→holes→board + blinds Next; SoftGate/toast not in payload;
 * Soft #5 `/`·`/ko` Quiet footer intact.
 */
import {
  localeFromPathname,
  rememberFooterLocalePick,
  t,
} from '../i18n/strings'
import {
  formatSeatCode,
  getSteps,
  handHasDraftProgress,
} from '../lib/poker'
import {
  areBlindsValid,
  canUndo,
  closeSettings,
  dismissDraftRestorePrompt,
  getFarthestStepIndex,
  getRewindPending,
  geoLocaleNeeded,
  getState,
  hydrateLocaleFromGeo,
  isDraftRestorePrompt,
  isNextSoftDisabled,
  isStepChipInJourney,
  isSummaryCard,
  navigate,
  peekToastDetail,
  resetHand,
  subscribe,
  switchLocaleInPlace,
  syncRouteFromHash,
  undoLast,
} from '../state'
import {
  bottomAd,
  loadAdSenseScript,
  topAd,
} from './ads'
import {
  clear,
  el,
} from './dom'
import { fitInputStrips, renderInputStrip } from './inputStrip'
import {
  renderPrivacy,
} from './privacy'
import { handleUndoKey } from './undoKey'
import {
  softGateBanner,
} from './softGate'
import {
  blindsError,
  explainModeToggle,
  go,
  languageSwitcher,
  renderLineStrip,
  renderStepPanel,
  requestStep,
  softHint,
} from './steps'


/**
 * Softwise M10b: stacked progress line on input screens — the single line
 * generator (lib/lineText) so input, Share card, Copy and PNG agree.
 * Tappable (jump back and re-enter) on the Line step only.
 * Soft E P2: Quiet collapsed 「지금까지 줄」 mid-input peek (default closed).
 * Soft-safe ①②⑤③: no SoftGate / % / pre-select; Soft 6 peek ≤ L1 Quiet.
 */
function softStackStrip(): HTMLElement | null {
  const { step } = getState()
  if (step === 'setup' || step === 'summary') return null
  // Softwise M10c: shown from the first question (dim defaults segment →
  // settings; hero seat / hole segments → jump back to edit).
  const strip = renderLineStrip({ interactive: step === 'actions' || step === 'board' })
  if (!strip) return null
  const ui = t()
  // Soft E Soft 4 Quiet: default closed — empty until press (Soft-safe ⑤).
  return el(
    'details',
    {
      className: 'linePeek',
      attrs: { 'data-soft-e': 'line-peek' },
    },
    [
      el('summary', {
        className: 'linePeekSummary',
        text: ui.linePeekSummary,
      }),
      strip,
    ],
  )
}

/** Softwise M10b: input screens get a quiet 「기록 마치기」 (Soft I1b; ends the record there). */
function isInputStep(step: string): boolean {
  return step === 'position' || step === 'cards' || step === 'actions' || step === 'board'
}

function toastMessage(): string | null {
  const ui = t()
  const { toast } = getState()
  if (toast === 'copied') return ui.copiedToast
  if (toast === 'shared') return ui.sharedToast
  if (toast === 'shareFailed') return ui.shareFailedToast
  if (toast === 'linkCopied') return ui.linkCopiedToast
  if (toast === 'linkWithoutKnownHoles') return ui.linkWithoutKnownHolesToast
  if (toast === 'linkWithoutEnding') return ui.linkWithoutEndingToast
  if (toast === 'linkWithoutTakeaway') return ui.linkWithoutTakeawayToast
  if (toast === 'linkTooLong') return ui.linkTooLongToast
  if (toast === 'actorRequired') return ui.actorRequired
  if (toast === 'sizeRequired') return ui.sizeRequired
  if (toast === 'villainNudge') return ui.villainNudgeToast
  if (toast === 'seatFullName') return peekToastDetail()
  if (toast === 'macroOpenExists') return ui.macroOpenExists
  if (toast === 'macroAlreadyLogged') return ui.macroAlreadyLogged
  // Soft F: seat name (BTN etc.), never `Hero`.
  if (toast === 'macroFoldToHeroNone') {
    const { hand } = getState()
    return ui.macroFoldToHeroNone(formatSeatCode(hand.heroPosition, hand.playerCount))
  }
  if (toast === 'macroFoldToHeroBlocked') return ui.macroFoldToHeroBlocked
  if (toast === 'imageSaved') return ui.imageSavedToast
  if (toast === 'imageShared') return ui.imageSharedToast
  if (toast === 'imageFailed') return ui.imageFailedToast
  return null
}

/** Fixed overlay toast — visible on every step (incl. Actions validation). */
function renderToastOverlay(): HTMLElement | null {
  const msg = toastMessage()
  if (!msg) return null
  const { toast } = getState()
  const isError =
    toast === 'actorRequired' ||
    toast === 'sizeRequired' ||
    toast === 'imageFailed' ||
    toast === 'shareFailed'
  // Soft Ultimate #4: quiet success chrome — never louder than the share card
  // Soft I2b UX Soft 6: the three link-drop notices share the same quiet style.
  const isShareSuccess =
    toast === 'copied' ||
    toast === 'shared' ||
    toast === 'imageSaved' ||
    toast === 'imageShared' ||
    toast === 'linkCopied' ||
    toast === 'linkWithoutKnownHoles' ||
    toast === 'linkWithoutEnding' ||
    toast === 'linkWithoutTakeaway'
  const overlay = el('div', {
    className: 'toastOverlay',
    attrs: { role: 'status', 'aria-live': 'polite' },
  }, [
    el('p', {
      className: [
        'toast',
        'toastBanner',
        isError ? 'toastError' : '',
        isShareSuccess ? 'toastSuccess' : '',
      ]
        .filter(Boolean)
        .join(' '),
      text: msg,
    }),
  ])
  if (isShareSuccess) keepToastOffShareCard(overlay)
  return overlay
}

/** Toast PR: the button the user last pressed (capture phase, any input). */
// The app re-renders on state change (and the button label may flip, e.g.
// 「이미지 저장」→「저장됨 ✓」), so the node is re-found by its class list and
// its index among buttons with that same class list.
// A confirm step (soft gate) may remove the last button pressed, so the most
// recent pressed button that still exists wins.
type Pressed = { node: Element; cls: string; idx: number }
// class list of the button plus its 3 nearest ancestors (removed dialogs never match)
const sig = (b: Element) => [b, b.parentElement, b.parentElement?.parentElement, b.parentElement?.parentElement?.parentElement].map((x) => x?.className ?? '').join('>')
let pressedHistory: Pressed[] = []
addEventListener('click', (e) => {
  const node = (e.target as Element | null)?.closest?.('button')
  if (!node) return
  const cls = sig(node)
  const idx = [...document.querySelectorAll('button')].filter((x) => sig(x) === cls).indexOf(node)
  pressedHistory = [{ node, cls, idx }, ...pressedHistory].slice(0, 4)
}, true)
function resolvePressed(p: Pressed): Element | null {
  if (p.node.isConnected) return p.node
  return [...document.querySelectorAll('button')].filter((x) => sig(x) === p.cls)[p.idx] ?? null
}
function pressedButton(): Element | null {
  let n: Element | null = null
  for (const p of pressedHistory) if ((n = resolvePressed(p))) break
  if (!n) return null
  if (n.checkVisibility?.() ?? n.getClientRects().length > 0) return n
  // Re-render closed its <details> (e.g. 「다른 공유」): anchor to that summary.
  return n.closest('details')?.querySelector('summary') ?? null
}

/**
 * Toast PR: a quiet share toast is anchored next to the button that was just
 * pressed (e.g. 「이미지 저장」) — never over that button, never over the share
 * card. Tries just above, then just below the button, inside the viewport.
 * Last resort: the card's outer padding strip, clear of every line of text.
 * Same toast, same text, same duration; follows the button on scroll/resize.
 */
const CONTROL_SEL =
  'button, a[href], summary, input, select, textarea, [role="button"], [tabindex]:not([tabindex="-1"]), h1, h2, h3, nav, .nav, .shareLabel'
/** Toast PR: rendered and inside the viewport (closed <details> bodies excluded). */
function shownInView(e: Element): boolean {
  if (e.closest('details:not([open]) > :not(summary)')) return false
  if (!(e.checkVisibility?.({ checkOpacity: true, checkVisibilityCSS: true }) ?? e.getClientRects().length > 0)) return false
  const r = e.getBoundingClientRect()
  return r.width >= 1 && r.height >= 1 && r.bottom > 0 && r.top < innerHeight
}
/** Toast PR: an element that paints text (or a playing card) of its own. */
function paintsContent(e: Element): boolean {
  return (
    [...e.childNodes].some((n) => n.nodeType === 3 && (n.textContent ?? '').trim() !== '') ||
    (e.childElementCount === 0 && (e.textContent ?? '').trim() !== '') ||
    /playingCard/.test(e.className.toString())
  )
}

/**
 * Toast PR: a quiet share toast never covers visible content. Spots must be
 * fully in view and 0px over every visible card element, heading, button,
 * link, form control and the bottom nav. Search order:
 *   1. just above / just below the button just pressed (or its <details> summary);
 *   2. elsewhere in view outside the card (nearest the top, e.g. under the header);
 *   3. over the top header bar / card padding — still clear of all text and controls.
 * There is no fixed-bottom fallback. Text, style and 1800ms are unchanged.
 */
function keepToastOffShareCard(overlay: HTMLElement): void {
  const GAP = 6
  // Start outside the viewport (not visibility:hidden, so the live region is
  // announced) until a clear spot is found: no flash at the CSS default.
  overlay.style.top = '-120px'
  overlay.style.bottom = 'auto'
  let hidden = false
  const place = () => {
    if (hidden) return
    if (!overlay.isConnected) {
      removeEventListener('scroll', place)
      removeEventListener('resize', place)
      return
    }
    const t = overlay.getBoundingClientRect()
    const h = t.height
    const card = document.querySelector('.shareCard')
    const c = card && shownInView(card) ? card.getBoundingClientRect() : null
    const hits = (top: number, r: DOMRect) =>
      top < r.bottom && top + h > r.top && r.right > t.left && r.left < t.right
    const mine = (e: Element) => overlay.contains(e)
    const cardContent = card
      ? [...card.querySelectorAll('*')].filter((e) => shownInView(e) && paintsContent(e))
      : []
    const pageContent = [...document.querySelectorAll('body *')].filter(
      (e) => !mine(e) && !(card && card.contains(e)) && shownInView(e) && paintsContent(e),
    )
    const controls = [...document.querySelectorAll(CONTROL_SEL)].filter((e) => !mine(e) && shownInView(e))
    const avoid = [...cardContent, ...pageContent, ...controls].map((e) => e.getBoundingClientRect())
    const free = (top: number) => top >= 0 && top + h <= innerHeight && !avoid.some((r) => hits(top, r))
    const outsideCard = (top: number) => !(c && hits(top, c))
    let top: number | null = null
    // 1. next to the anchor
    const btn = pressedButton()
    if (btn && shownInView(btn)) {
      const b = btn.getBoundingClientRect()
      top = [b.top - GAP - h, b.bottom + GAP].find((y) => free(y) && outsideCard(y)) ?? null
    }
    // 2. elsewhere in view outside the card; 3. header bar / card padding
    const ys: number[] = []
    for (let y = 0; y + h <= innerHeight; y++) ys.push(y)
    if (top == null) top = ys.find((y) => free(y) && outsideCard(y)) ?? null
    if (top == null) top = ys.find((y) => free(y)) ?? null
    if (top == null) {
      // No clear 34px strip in view (e.g. 223×482 mid-card): park the toast
      // outside the viewport. It stays in the a11y tree, so the live region
      // still announces; the 「· 저장됨」 marker is the visual confirmation.
      hidden = true
      overlay.style.top = `${-Math.ceil(h) - 16}px`
      overlay.style.bottom = 'auto'
      return
    }
    overlay.style.top = `${Math.round(top)}px`
    overlay.style.bottom = 'auto'
  }
  addEventListener('scroll', place, { passive: true })
  addEventListener('resize', place)
  queueMicrotask(place)
  requestAnimationFrame(place)
  setTimeout(place, 0) // rAF is paused in background tabs
}


function renderBottomNav(
  step: string,
  stepIndex: number,
  nextDisabled: boolean,
  blindsOk: boolean,
): HTMLElement | null {
  const ui = t()
  const steps = getSteps()
  if (step === 'position') return null
  // Soft I1b: the summary card carries its own [되돌리기][공유] row.
  if (isSummaryCard()) return null
  if (step === 'setup') {
    return el('div', { className: 'nav navSettings' }, [
      el('button', {
        className: 'btn btnQuiet settingsBackBtn',
        attrs: {
          type: 'button',
          disabled: !blindsOk ? true : undefined,
          title: !blindsOk ? ui.errorBlinds : undefined,
        },
        text: ui.settingsBack,
        on: { click: () => closeSettings() },
      }),
    ])
  }
  // Soft I1a: input screens read [되돌리기][여기서 끝]. 되돌리기 is one tap
  // (no confirm, no toast): undo the last input change; with nothing to undo
  // it steps back like the old 「이전」. A pending 「여기부터 다시」 keeps the
  // share step closed (dimmed items never reach share / Copy / PNG / link).
  const rewindPending = getRewindPending() != null
  const prevBtn = el('button', {
    className: 'btn btnQuiet navPrevBtn',
    attrs: {
      type: 'button',
      'data-soft-i1a': 'undo',
      disabled: stepIndex <= 0 && !canUndo() && !rewindPending ? true : undefined,
    },
    text: ui.undo,
    on: { click: () => (undoLast() ? undefined : go(-1)) },
  })
  if (isInputStep(step)) {
    return el('div', { className: 'nav' }, [
      prevBtn,
      el('button', {
        className: 'btn btnQuiet endHereBtn',
        attrs: { type: 'button', disabled: rewindPending ? true : undefined },
        // Soft I1b: 「기록 마치기」 (factual; reads right after a fold too) → summary card.
        text: ui.finishRecord,
        on: { click: () => requestStep('summary', { card: true }) },
      }),
    ])
  }
  return el('div', { className: 'nav' }, [
    el('button', {
      className: 'btn',
      attrs: { type: 'button', disabled: stepIndex <= 0 ? true : undefined },
      text: ui.prev,
      on: { click: () => go(-1) },
    }),
    el('button', {
      className: 'btn btnPrimary',
      attrs: { type: 'button', disabled: nextDisabled ? true : undefined },
      text: stepIndex >= steps.length - 2 ? ui.nextSummary : ui.next,
      on: { click: () => go(1) },
    }),
  ])
}

function renderAppShell(): HTMLElement {
  const ui = t()
  const { step, hand, captureMode } = getState()
  const steps = getSteps()
  // Softwise M10b: legacy `board` step folds into the Line step.
  const stepIndex = Math.max(
    0,
    steps.findIndex((s) => s.id === (step === 'board' ? 'actions' : step)),
  )
  const hint = softHint(step)
  const blindsErr = blindsError()
  const blindsOk = areBlindsValid(hand)
  // Soft stepwise M3: Soft-disable Next on cards (<2 holes) / actions (0 actions).
  // Setup blinds gate stays; board Next stays enabled (board optional).
  const nextDisabled =
    stepIndex >= steps.length - 1 ||
    (step === 'setup' && !blindsOk) ||
    isNextSoftDisabled(step, hand)
  const farthest = getFarthestStepIndex()
  // Softwise M10d: the draft banner is only a resume offer on a fresh app
  // load that restored a saved draft. It never appears while the current
  // hand is being entered (it used to pop in after the first hole card and
  // push the input down), so the layout never shifts mid-input.
  const showDraftResume =
    !captureMode && step !== 'summary' && isDraftRestorePrompt() && handHasDraftProgress(hand)

  // Softwise M4 Quiet: draft banner must not outrank step L1 / Soft #3 chronology.
  const newHandBanner = showDraftResume
    ? el(
        'div',
        {
          className: 'newHandBanner newHandBannerRestore newHandBannerQuiet',
          attrs: { role: 'status' },
        },
        [
          el('p', {
            className: 'newHandBannerHint',
            text: ui.draftRestoredHint,
          }),
          el('div', { className: 'newHandBannerActions' }, [
            el('button', {
              className: 'btn btnQuiet newHandBannerBtn',
              attrs: { type: 'button' },
              text: ui.draftContinue,
              on: { click: () => dismissDraftRestorePrompt() },
            }),
            el('button', {
              className: 'btn btnGhost newHandBannerBtn',
              attrs: { type: 'button' },
              text: ui.newHand,
              on: { click: () => resetHand() },
            }),
          ]),
        ],
      )
    : null

  loadAdSenseScript()

  return el('div', {
    className: step === 'summary' ? 'shell shellSummary' : 'shell',
    attrs: captureMode && step === 'summary' ? { 'data-capture': '1' } : {},
  }, [
    renderToastOverlay(),
    el('div', { className: 'app' }, [
      el('header', {
        className: [
          'header',
          step !== 'position' && (step !== 'summary' || isSummaryCard()) ? 'headerQuiet' : '',
        ]
          .filter(Boolean)
          .join(' '),
      }, [
        el('div', { className: 'headerTop' }, [
          el('h1', { className: 'brand', text: ui.brand }),
          el('div', { className: 'headerTopTools' }, [
            explainModeToggle(),
            languageSwitcher(),
          ]),
        ]),
        // Softwise M10c: the seat question is the first screen.
        step === 'position'
          ? el('p', { className: 'tagline', text: ui.tagline })
          : null,
      ]),
      newHandBanner,
      topAd(),
      // Softwise M10a: no numeric percent (felt like an exam). Quiet step dots;
      // aria says "step N of M" in words via i18n — never a percent sign.
      // Softwise M10b: no `n / N` text either — dots only.
      el('div', { className: 'progressBlock progressBlockQuiet' }, [
        el(
          'ol',
          {
            className: 'progressDots',
            attrs: {
              'aria-label': ui.progressAria(
                stepIndex + 1,
                steps.length,
                steps[stepIndex]!.label,
              ),
            },
          },
          steps.map((s, i) =>
            el('li', {
              className: [
                'progressDot',
                i < stepIndex ? 'progressDotDone' : '',
                i === stepIndex ? 'progressDotCurrent' : '',
              ]
                .filter(Boolean)
                .join(' '),
              attrs: {
                'aria-hidden': 'true',
                'data-step': s.id,
              },
            }),
          ),
        ),
      ]),
      softStackStrip(),
      // Soft I1a: sticky compact chronological strip (input view + pot bb).
      renderInputStrip(),
      // Soft I1b: nothing with an accent above the summary card (no step chips).
      isSummaryCard() ? null : el(
        'nav',
        {
          className: ['steps', 'stepsStepwise'].filter(Boolean).join(' '),
          attrs: { 'aria-label': ui.stepsAria },
        },
        steps
          .map((s, i) => ({ s, i }))
          // Soft stepwise M2 Quiet: only current, visited, and immediate +1
          // — do not show 4·5·6 as equal targets on step 1.
          .filter(({ s }) => isStepChipInJourney(s.id))
          .map(({ s, i }) => {
            const visited = i <= farthest && s.id !== step
            const isNextUnlock = i === farthest + 1 && s.id !== step
            return el('button', {
              className: [
                'stepChip',
                s.id === step ? 'stepChipActive' : '',
                visited ? 'stepChipDone' : '',
                isNextUnlock ? 'stepChipNext' : '',
              ]
                .filter(Boolean)
                .join(' '),
              attrs: {
                type: 'button',
                'aria-current': s.id === step ? 'step' : undefined,
              },
              text: s.label,
              on: { click: () => requestStep(s.id) },
            })
          }),
      ),
      renderStepPanel(step),
      blindsErr && step === 'setup'
        ? el('p', {
            className: 'hardError',
            attrs: { role: 'alert' },
            text: blindsErr,
          })
        : null,
      softGateBanner(),
      hint ? el('p', { className: 'softHint', text: hint }) : null,
      // Softwise M10b: 「요약 보기」 merged into a quiet 「여기서 끝」 on input
      // screens (ends the record there; nothing after is filled). The Line step
      // has no filled Next — its only filled button is the custom-size 확인.
      // Softwise M10c: no filled 「다음」 on the seat / hole / settings screens —
      // a seat tap and the second hole card advance on their own. Back / edit
      // controls stay quiet. The Line step's only filled button is the
      // custom-size 확인; Summary keeps its own bar.
      renderBottomNav(step, stepIndex, nextDisabled, blindsOk),
      bottomAd(),
      // Soft residual 5: Quiet EN→KO path discovery (opt-in <a>; never force locale).
      // Reciprocal Quiet / link on /ko. No extra crawlable locales.
      // Soft I2a: a click also remembers the pick (same markup; plain navigation).
      // Link follows the locale on screen; same-page link switches in place.
      el('footer', { className: 'footer' }, [
        getState().locale === 'ko'
          ? el('a', {
              className: 'linkBtn footerLocalePath',
              attrs: {
                href: '/',
                hreflang: 'en',
                lang: 'en',
                'aria-label': ui.enPathLinkAria,
              },
              text: ui.enPathLink,
              on: { click: (e) => footerLocaleClick(e, 'en') },
            })
          : el('a', {
              className: 'linkBtn footerLocalePath',
              attrs: {
                href: '/ko',
                hreflang: 'ko',
                lang: 'ko',
                'aria-label': ui.koPathLinkAria,
              },
              text: ui.koPathLink,
              on: { click: (e) => footerLocaleClick(e, 'ko') },
            }),
        el('button', {
          className: 'linkBtn',
          attrs: { type: 'button' },
          text: ui.privacyLink,
          on: { click: () => navigate('privacy') },
        }),
      ]),
    ]),
  ])
}

/** Last step/view we scrolled+focused — skip in-step re-renders (chips/sliders). */
let lastScrollFocusKey: string | null = null

function scrollWindowToTop(): void {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
}

/** On step change only: scroll to top and focus the new panel heading once. */
function maybeScrollFocusStep(mount: HTMLElement): void {
  const { view, step } = getState()
  // Soft I1b: card view → full share is a new screen (opens at the top too).
  const key = view === 'privacy' ? 'privacy' : `app:${step}${isSummaryCard() ? ':card' : ''}`
  if (key === lastScrollFocusKey) return
  lastScrollFocusKey = key

  scrollWindowToTop()

  // Defer focus until the new panel is in the DOM / layout settled.
  requestAnimationFrame(() => {
    const heading =
      (mount.querySelector('.panelTitle') as HTMLElement | null) ??
      (mount.querySelector('h1.brand, h2') as HTMLElement | null)
    if (!heading) return
    if (!heading.hasAttribute('tabindex')) heading.tabIndex = -1
    try {
      heading.focus({ preventScroll: false })
    } catch {
      heading.focus()
    }
  })
}

function renderRoot(mount: HTMLElement): void {
  clear(mount)
  const { view } = getState()
  if (view === 'privacy') {
    mount.append(
      el('div', { className: 'shell' }, [
        el('div', { className: 'app' }, [
          languageSwitcher(),
          renderPrivacy(),
        ]),
      ]),
    )
    maybeScrollFocusStep(mount)
    return
  }
  mount.append(renderAppShell())
  fitInputStrips(mount)
  maybeScrollFocusStep(mount)
}

/**
 * Footer locale link click. Link to another page (/ ↔ /ko): remember the pick
 * and navigate as before. Link to the page already open: switch in place.
 */
function footerLocaleClick(e: Event, target: 'en' | 'ko'): void {
  const onKo = localeFromPathname() === 'ko'
  const samePage = target === 'ko' ? onKo : !onKo
  if (!samePage) {
    rememberFooterLocalePick(target)
    return
  }
  // Same page: no reload — switch the screen in place (still remembered).
  e.preventDefault()
  switchLocaleInPlace(target)
}

/** Geo hold on a first visit: wait this long at most before the first render. */
const GEO_HOLD_MS = 400

export function mountApp(mount: HTMLElement): void {
  syncRouteFromHash()
  window.addEventListener('hashchange', () => {
    syncRouteFromHash()
  })
  subscribe(() => renderRoot(mount))
  // Soft I1a: Ctrl/Cmd+Z outside text fields = one undo; refit strip on resize/zoom.
  document.addEventListener('keydown', (e) => {
    handleUndoKey(e)
  })
  window.addEventListener('resize', () => fitInputStrips(mount))
  // Footer locale PR: on a first visit that asks geo, hold the first render
  // (≤ GEO_HOLD_MS) so the page does not paint English and then flip. The
  // landing locale is unchanged: a later geo answer still applies as before.
  if (!geoLocaleNeeded()) {
    renderRoot(mount)
    return
  }
  let rendered = false
  const first = () => { if (!rendered) { rendered = true; renderRoot(mount) } }
  const hold = setTimeout(first, GEO_HOLD_MS)
  void hydrateLocaleFromGeo().finally(() => {
    clearTimeout(hold)
    // Geo may change locale without persist — render once (or re-render).
    if (rendered) renderRoot(mount)
    else first()
  })
}
